/**
 * Shared atoms for the measure detail sections (authenticated + public).
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import RichContent from '../../RichContent';
import { OpenExternalIcon } from '../../icons';
import { provenanceLabel, type ProvenanceEntry } from '../../../constants/ballotInitiatives';
import { formatCalendarDate } from '../initiativeDisplay';

/**
 * RichContent renders through `prose`, whose paragraph margins open a gap above
 * the first line and below the last. Trim them so a block sits where its box is.
 */
export const PROSE_TRIM = '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0';

/** Muted secondary text (slate-500), the design's captions and sub-labels. */
export const MUTED_TEXT = 'text-slate-500 dark:text-text-dark-tertiary';

/** In-page and external text links on the measure detail. */
export const DETAIL_LINK =
  'text-primary-600 hover:text-primary-800 hover:underline underline-offset-2 dark:text-primary-400 dark:hover:text-primary-300';

/** A section heading: Poppins 19px, the card heading on every detail section. */
export const DetailHeading: React.FC<{ id?: string; children: React.ReactNode; className?: string }> = ({
  id,
  children,
  className = '',
}) => (
  <h2
    id={id}
    className={`font-display text-[19px] font-semibold tracking-[-0.01em] text-text-primary dark:text-text-dark-primary ${className}`}
  >
    {children}
  </h2>
);

/** The uppercase label over a value or a sub-list (Inter 700, 12px, tracked). */
export const DetailLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = MUTED_TEXT,
}) => (
  <div className={`text-xs font-bold uppercase tracking-[0.08em] ${className}`}>{children}</div>
);

/** An external link that says so: new tab, no opener, the external-link glyph. */
export const ExternalTextLink: React.FC<{
  href: string;
  children: React.ReactNode;
  className?: string;
}> = ({ href, children, className = '' }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center gap-1 ${DETAIL_LINK} ${className}`}
  >
    {children}
    <OpenExternalIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
  </a>
);

/**
 * A detail section card: the heading on the left, an optional aside (a source
 * line, a link) on the right, then the body.
 */
export const DetailCard: React.FC<{
  id: string;
  title: string;
  /** One muted line under the heading (13px). */
  subLine?: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, title, subLine, aside, children }) => (
  <section className="card" aria-labelledby={id}>
    <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <div className="min-w-0">
        <DetailHeading id={id}>{title}</DetailHeading>
        {subLine && <p className={`mt-0.5 text-[13px] ${MUTED_TEXT}`}>{subLine}</p>}
      </div>
      {aside && <div className="text-xs">{aside}</div>}
    </div>
    {children}
  </section>
);

/**
 * One collapsible row of a stacked card (the Official record): title and
 * sub-label on the left, a chevron that turns when open. Native <details>, so
 * keyboard and screen-reader behaviour come with it. Closed by default.
 */
export const DisclosureRow: React.FC<{
  id?: string;
  title: string;
  subLabel?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, title, subLabel, children }) => (
  <details id={id} className="group border-b border-border-light last:border-b-0 dark:border-border-dark">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 sm:px-6 [&::-webkit-details-marker]:hidden">
      <span className="min-w-0">
        <span className="block text-base font-semibold text-text-primary dark:text-text-dark-primary">{title}</span>
        {subLabel && <span className={`mt-0.5 block text-[13px] ${MUTED_TEXT}`}>{subLabel}</span>}
      </span>
      <ChevronDown
        className="h-5 w-5 flex-shrink-0 text-primary-400 transition-transform group-open:rotate-180"
        aria-hidden="true"
      />
    </summary>
    <div className="px-5 pb-5 sm:px-6">{children}</div>
  </details>
);

export type TimelineStepState = 'done' | 'current' | 'upcoming';

export interface TimelineStep {
  key: string;
  label: string;
  /** Already formatted for display. */
  date: string;
  state: TimelineStepState;
  sourceUrl?: string | null;
  sourceLabel?: string | null;
}

const TIMELINE_DOT: Record<TimelineStepState, string> = {
  done: 'bg-slate-400',
  current: 'bg-primary-500',
  upcoming: 'border-2 border-action bg-white dark:bg-background-dark-secondary',
};

/**
 * A dated path, left to right on wide screens and top to bottom on a phone:
 * a dot per step (slate when done, purple for the current step, an orange ring
 * for an upcoming one), a connector to the next, then the label, date and an
 * optional source link. Shared by the measure and person detail pages.
 */
export const DetailTimeline: React.FC<{ steps: TimelineStep[] }> = ({ steps }) => (
  <ol className="flex flex-col gap-4 md:flex-row md:gap-0">
    {steps.map((step, index) => (
      <li
        key={step.key}
        className="flex min-w-0 gap-3 md:flex-1 md:flex-col md:gap-0"
        aria-current={step.state === 'current' ? 'step' : undefined}
      >
        <div className="flex items-center pt-1 md:mb-3 md:pt-0">
          <span className={`h-3 w-3 flex-shrink-0 rounded-full ${TIMELINE_DOT[step.state]}`} aria-hidden="true" />
          {index < steps.length - 1 && (
            <span
              className={`mx-2 hidden h-0.5 flex-1 md:block ${step.state === 'upcoming' ? 'bg-border-light dark:bg-border-dark' : 'bg-slate-200 dark:bg-slate-600'}`}
              aria-hidden="true"
            />
          )}
        </div>
        <div className="min-w-0 md:pr-4">
          <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">{step.label}</div>
          <div className={`text-xs ${MUTED_TEXT}`}>
            {step.date}
            {step.state === 'upcoming' && <span className="sr-only"> (upcoming)</span>}
          </div>
          {step.sourceUrl && (
            <ExternalTextLink href={step.sourceUrl} className="text-xs">
              {step.sourceLabel || 'Source'}
            </ExternalTextLink>
          )}
        </div>
      </li>
    ))}
  </ol>
);

export const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  /** Drop the card chrome, for sections the hero already provides a surface for. */
  bare?: boolean;
}> = ({ title, children, bare = false }) => {
  if (bare) {
    return (
      <div>
        {title && (
          <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary mb-3">{title}</h3>
        )}
        {children}
      </div>
    );
  }
  return (
    <div className="card">
      <DetailHeading className="mb-4">{title}</DetailHeading>
      {children}
    </div>
  );
};

/** The provenance label line on its own, for a card whose one declaration
 *  covers more than one block (the fiscal statement + breakdown, v2 PRD §5.6.3).
 *  Renders nothing when the entry carries no label. */
export const ProvenanceNote: React.FC<{ provenance?: ProvenanceEntry }> = ({ provenance }) => {
  const label = provenanceLabel(provenance);
  if (!label) return null;
  return (
    <p className="mt-1.5 text-xs italic text-text-tertiary dark:text-text-dark-tertiary">
      {label}
      {provenance?.reviewed_at ? ` · reviewed ${formatCalendarDate(provenance.reviewed_at)}` : ''}
    </p>
  );
};

/** Body text with its provenance label, when the field carries one (PRD §5.6.7 —
 *  the label rule applies to every reader surface, public included). */
export const ProvenancedText: React.FC<{
  text: string;
  provenance?: ProvenanceEntry;
}> = ({ text, provenance }) => (
  <div>
    <RichContent html={text} className="text-sm text-text-secondary dark:text-text-dark-secondary" />
    <ProvenanceNote provenance={provenance} />
  </div>
);

/** "$276K" — a side total at a glance. Exact amounts sit on each committee row. */
export const formatCompactMoney = (value: number | null | undefined): string => {
  const n = Number(value ?? 0);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: Math.abs(n) >= 1_000_000 ? 1 : 0,
  });
};

export const formatMoney = (value: number | null | undefined): string =>
  value != null && Number(value) !== 0
    ? Number(value).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '$0';
